import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Trash2, RotateCcw, AlertTriangle, Film } from 'lucide-react';
import { API } from './shared';

export function ConfirmModal({ title, message, confirmLabel = 'Supprimer', cancelLabel = 'Annuler', danger = true, onConfirm, onCancel, light }) {
  return (
    <div className="fixed inset-0 z-[999] bg-black/70 flex items-center justify-center p-4" onClick={onCancel} data-testid="confirm-modal">
      <div className={`rounded-xl border w-full max-w-md p-5 ${light ? 'bg-white border-gray-200' : 'bg-[#1a0e2e] border-white/10'}`} onClick={e => e.stopPropagation()}>
        <div className="flex items-start gap-3">
          <span className={`w-9 h-9 rounded-full flex items-center justify-center flex-none ${danger ? 'bg-red-500/15' : 'bg-[#E9641A]/15'}`}>
            <AlertTriangle size={16} className={danger ? 'text-red-500' : 'text-[#E9641A]'} />
          </span>
          <div>
            <h3 className={`text-sm font-bold mb-1 ${light ? 'text-gray-900' : 'text-white'}`}>{title}</h3>
            <p className={`text-xs leading-relaxed ${light ? 'text-gray-500' : 'text-white/55'}`}>{message}</p>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onCancel} data-testid="confirm-modal-cancel"
            className={`px-4 py-2 text-xs rounded-lg border transition-colors ${light ? 'text-gray-500 border-gray-200 hover:text-gray-800' : 'text-white/50 border-white/10 hover:text-white'}`}>
            {cancelLabel}
          </button>
          <button onClick={onConfirm} data-testid="confirm-modal-ok"
            className={`px-4 py-2 text-xs font-semibold rounded-lg text-white transition-colors ${danger ? 'bg-red-600 hover:bg-red-500' : 'bg-[#E9641A] hover:bg-[#FE975E]'}`}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export function TrashTab({ theme }) {
  const light = theme === 'light';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(null); // {type: 'purge'|'empty', item}
  const cardCls = light ? 'bg-white border-gray-200 shadow-sm' : 'bg-[#1a0e2e] border-white/5';
  const hintCls = light ? 'text-gray-400' : 'text-white/35';

  const load = useCallback(() => {
    axios.get(`${API}/admin/trash`, { withCredentials: true })
      .then(r => setItems(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const restore = async (item) => {
    await axios.post(`${API}/admin/trash/${item.id}/restore`, {}, { withCredentials: true }).catch(() => {});
    load();
  };

  const doConfirm = async () => {
    const c = confirm; setConfirm(null);
    try {
      if (c.type === 'empty') await axios.delete(`${API}/admin/trash`, { withCredentials: true });
      else await axios.delete(`${API}/admin/trash/${c.item.id}`, { withCredentials: true });
    } catch { /* reload anyway */ }
    load();
  };

  return (
    <div data-testid="trash-tab">
      <div className="flex items-center justify-between mb-1">
        <h1 className={`text-2xl font-bold ${light ? 'text-gray-900' : 'text-white'}`}>Corbeille ({items.length})</h1>
        {items.length > 0 && (
          <button onClick={() => setConfirm({ type: 'empty' })} data-testid="trash-empty-btn"
            className="flex items-center gap-1.5 bg-red-600 hover:bg-red-500 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors">
            <Trash2 size={13} /> Vider la corbeille
          </button>
        )}
      </div>
      <p className={`text-xs mb-6 ${hintCls}`}>Les contenus supprimés ne sont plus visibles par les utilisateurs. Restaurez-les pour les remettre à leur place, ou supprimez-les définitivement.</p>

      {loading ? (
        <p className={`text-center text-sm py-10 ${hintCls}`}>Chargement...</p>
      ) : items.length === 0 ? (
        <div className={`rounded-xl border p-10 text-center ${cardCls}`} data-testid="trash-empty-state">
          <Trash2 size={28} className={`mx-auto mb-3 ${light ? 'text-gray-300' : 'text-white/15'}`} />
          <p className={`text-sm ${hintCls}`}>La corbeille est vide.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map(item => (
            <div key={item.id} className={`flex items-center gap-3 rounded-xl border p-3 ${cardCls}`} data-testid={`trash-item-${item.id}`}>
              <div className={`w-20 h-12 rounded-lg overflow-hidden flex-none flex items-center justify-center ${light ? 'bg-gray-100' : 'bg-white/5'}`}>
                {item.thumbnail_url || item.banner_url || item.poster_url
                  ? <img src={item.thumbnail_url || item.banner_url || item.poster_url} alt={item.title} className="w-full h-full object-cover grayscale opacity-70" />
                  : <Film size={16} className={hintCls} />}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-xs font-semibold truncate ${light ? 'text-gray-800' : 'text-white'}`}>{item.title}</p>
                <p className={`text-[10px] ${hintCls}`}>
                  {item.type === 'series' ? 'Série' : item.type === 'game' ? 'Jeu' : item.type === 'documentaire' ? 'Documentaire' : item.type === 'jeunesse' ? 'Jeunesse' : 'Film'}
                  {item.deleted_at && <> · supprimé le {new Date(item.deleted_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</>}
                </p>
              </div>
              <button onClick={() => restore(item)} data-testid={`trash-restore-${item.id}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-semibold border transition-colors ${light ? 'border-gray-200 text-gray-600 hover:border-green-500 hover:text-green-600' : 'border-white/10 text-white/60 hover:border-green-500 hover:text-green-400'}`}>
                <RotateCcw size={11} /> Restaurer
              </button>
              <button onClick={() => setConfirm({ type: 'purge', item })} data-testid={`trash-purge-${item.id}`}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-semibold bg-red-600/90 hover:bg-red-500 text-white transition-colors">
                <Trash2 size={11} /> Supprimer définitivement
              </button>
            </div>
          ))}
        </div>
      )}

      {confirm && (
        <ConfirmModal light={light}
          title={confirm.type === 'empty' ? 'Vider la corbeille ?' : 'Supprimer définitivement ?'}
          message={confirm.type === 'empty'
            ? `Les ${items.length} contenu(s) de la corbeille seront supprimés définitivement, ainsi que leurs épisodes. Cette action est irréversible.`
            : `« ${confirm.item.title} » sera supprimé définitivement, ainsi que ses épisodes. Cette action est irréversible.`}
          confirmLabel={confirm.type === 'empty' ? 'Tout supprimer' : 'Supprimer définitivement'}
          onConfirm={doConfirm} onCancel={() => setConfirm(null)} />
      )}
    </div>
  );
}
